import React from 'react';
import { Card, CardBody, Flex, Box, Text, Spinner, Icon, Tooltip } from '@chakra-ui/react';
import {
  Money,
  CurrencyDollarSimple,
  Buildings,
  Intersect,
  XCircle,
  WarningCircle,
  TrendDown,
  Prohibit,
} from 'phosphor-react';

interface TaxRegimeSummaryCardsProps {
  totalLucroPresumido: number;
  totalSimplesNacional: number;
  totalEmpresas: number;
  totalSimplesGeral: number;
  totalPresumidoGeral: number;
  totalSemFaturamento: number;
  simplesSemFaturamento: number;
  lucroSemFaturamento: number;
  currentRegime: string | null;
  isTodasFaturadasSelected?: boolean;
  isSemFaturamentoSelected?: boolean;
  isLoading: boolean;
  isLoadingAlternative: boolean;
  // Alertas
  alertasLimiteExcedido?: number;
  alertasLimiteProximo?: number;
  alertasFaturamentoBaixo?: number;
  // eslint-disable-next-line no-unused-vars
  onSelectRegime: (regime: 'lucro_presumido' | 'simples_nacional') => void;
  // eslint-disable-next-line no-unused-vars
  onShowAllFaturadas?: () => void;
  // eslint-disable-next-line no-unused-vars
  onShowSemFaturamento?: () => void;
  // eslint-disable-next-line no-unused-vars
  onResetToDefault?: () => void;
}

export function TaxRegimeSummaryCards({
  totalLucroPresumido,
  totalSimplesNacional,
  totalEmpresas,
  totalSimplesGeral,
  totalPresumidoGeral,
  totalSemFaturamento,
  simplesSemFaturamento,
  lucroSemFaturamento,
  currentRegime,
  isTodasFaturadasSelected = false,
  isSemFaturamentoSelected = false,
  isLoading,
  isLoadingAlternative,
  alertasLimiteExcedido = 0,
  alertasLimiteProximo = 0,
  alertasFaturamentoBaixo = 0,
  onSelectRegime,
  onShowAllFaturadas,
  onShowSemFaturamento,
  onResetToDefault,
}: TaxRegimeSummaryCardsProps) {
  const totalAlertas = alertasLimiteExcedido + alertasLimiteProximo + alertasFaturamentoBaixo;
  const isLucroPresumidoLoading =
    currentRegime === 'lucro_presumido' ? isLoading : isLoadingAlternative;
  const isSimplesNacionalLoading =
    currentRegime === 'simples_nacional' ? isLoading : isLoadingAlternative;

  // Calcular porcentagens em relação ao total geral de empresas
  const porcentagemSimples =
    totalEmpresas > 0 ? ((totalSimplesNacional / totalEmpresas) * 100).toFixed(1) : '0.0';
  const porcentagemPresumido =
    totalEmpresas > 0 ? ((totalLucroPresumido / totalEmpresas) * 100).toFixed(1) : '0.0';

  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      gap={4}
      mb={6}
      flexWrap="wrap"
      justify={{ base: 'center', lg: 'flex-start' }}
    >
      <Card
        shadow="sm"
        bg="white"
        borderRadius="lg"
        cursor="pointer"
        onClick={onResetToDefault}
        _hover={{ shadow: 'md', bg: 'gray.50' }}
        border="1px solid"
        borderColor="gray.200"
        w={{ base: '100%', sm: '280px' }}
        maxW="280px"
        transition="all 0.2s"
      >
        <CardBody py={3} px={4} position="relative">
          {isLoading || isLoadingAlternative ? (
            <Flex align="center" justify="center" h="50px">
              <Spinner size="sm" color="purple.500" />
            </Flex>
          ) : (
            <Flex align="center" gap={4} justify="space-between">
              <Flex align="center" gap={3}>
                <Box p={1.5} borderRadius="lg" bg="purple.50">
                  <Buildings color="#805AD5" size={20} />
                </Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={0.5}>
                    Total de Empresas
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="gray.800">
                    {totalEmpresas}
                  </Text>
                </Box>
              </Flex>
              <Box textAlign="right" borderLeft="1px" borderColor="gray.200" pl={3}>
                <Text fontSize="2xs" color="gray.600" mb={0.5}>
                  Simples:{' '}
                  <Text as="span" fontWeight="semibold" color="yellow.600">
                    {totalSimplesGeral}
                  </Text>
                </Text>
                <Text fontSize="2xs" color="gray.600">
                  Lucro:{' '}
                  <Text as="span" fontWeight="semibold" color="teal.600">
                    {totalPresumidoGeral}
                  </Text>
                </Text>
              </Box>
            </Flex>
          )}
        </CardBody>
      </Card>

      <Card
        shadow="sm"
        bg={isTodasFaturadasSelected ? 'orange.50' : 'white'}
        borderRadius="lg"
        cursor="pointer"
        onClick={onShowAllFaturadas}
        _hover={{ shadow: 'md', bg: isTodasFaturadasSelected ? 'orange.100' : 'gray.50' }}
        border={isTodasFaturadasSelected ? '2px solid' : '1px solid'}
        borderColor={isTodasFaturadasSelected ? 'orange.300' : 'gray.200'}
        w={{ base: '100%', sm: '280px', md: '300px' }}
        minW={{ base: '100%', sm: '280px' }}
        transition="all 0.2s"
      >
        <CardBody py={3} px={4} position="relative">
          {isLoading || isLoadingAlternative ? (
            <Flex align="center" justify="center" h="50px">
              <Spinner size="sm" color="orange.500" />
            </Flex>
          ) : (
            <Flex
              align="center"
              gap={{ base: 2, sm: 3, md: 4 }}
              justify="space-between"
              flexWrap="wrap"
            >
              <Flex align="center" gap={{ base: 2, sm: 3 }} flex="1" minW="140px">
                <Box p={1.5} borderRadius="lg" bg="orange.50" flexShrink={0}>
                  <Intersect color="#ED8936" size={20} />
                </Box>
                <Box minW={0} flex="1">
                  <Text
                    fontSize={{ base: '2xs', sm: 'xs' }}
                    color="gray.500"
                    fontWeight="medium"
                    mb={0.5}
                    noOfLines={1}
                  >
                    Empresas Faturadas
                  </Text>
                  <Text
                    fontSize={{ base: 'md', sm: 'lg' }}
                    fontWeight="bold"
                    color="gray.800"
                    noOfLines={1}
                  >
                    {totalSimplesNacional + totalLucroPresumido}
                  </Text>
                </Box>
              </Flex>
              <Box
                textAlign="right"
                borderLeft="1px"
                borderColor="gray.200"
                pl={{ base: 2, sm: 3 }}
                flexShrink={0}
                minW="80px"
              >
                <Text fontSize="2xs" color="gray.600" mb={0.5} whiteSpace="nowrap">
                  Simples:{' '}
                  <Text as="span" fontWeight="semibold" color="yellow.600">
                    {totalSimplesNacional}
                  </Text>
                </Text>
                <Text fontSize="2xs" color="gray.600" whiteSpace="nowrap">
                  Lucro:{' '}
                  <Text as="span" fontWeight="semibold" color="teal.600">
                    {totalLucroPresumido}
                  </Text>
                </Text>
              </Box>
            </Flex>
          )}
        </CardBody>
      </Card>

      {/* Card Sem Faturamento */}
      <Card
        shadow="sm"
        bg={isSemFaturamentoSelected ? 'gray.100' : 'white'}
        borderRadius="lg"
        cursor="pointer"
        onClick={onShowSemFaturamento}
        _hover={{ shadow: 'md', bg: isSemFaturamentoSelected ? 'gray.200' : 'gray.50' }}
        border={isSemFaturamentoSelected ? '2px solid' : '1px solid'}
        borderColor={isSemFaturamentoSelected ? 'gray.400' : 'gray.200'}
        w={{ base: '100%', sm: '280px', md: '300px' }}
        minW={{ base: '100%', sm: '280px' }}
        transition="all 0.2s"
      >
        <CardBody py={3} px={4} position="relative">
          {isLoading || isLoadingAlternative ? (
            <Flex align="center" justify="center" h="50px">
              <Spinner size="sm" color="gray.500" />
            </Flex>
          ) : (
            <Flex
              align="center"
              gap={{ base: 2, sm: 3, md: 4 }}
              justify="space-between"
              flexWrap="wrap"
            >
              <Flex align="center" gap={{ base: 2, sm: 3 }} flex="1" minW="140px">
                <Box p={1.5} borderRadius="lg" bg="gray.100" flexShrink={0}>
                  <Prohibit color="#718096" size={20} />
                </Box>
                <Box minW={0} flex="1">
                  <Text
                    fontSize={{ base: '2xs', sm: 'xs' }}
                    color="gray.500"
                    fontWeight="medium"
                    mb={0.5}
                    noOfLines={1}
                  >
                    Sem Faturamento
                  </Text>
                  <Text
                    fontSize={{ base: 'md', sm: 'lg' }}
                    fontWeight="bold"
                    color="gray.800"
                    noOfLines={1}
                  >
                    {totalSemFaturamento}
                  </Text>
                </Box>
              </Flex>
              <Box
                textAlign="right"
                borderLeft="1px"
                borderColor="gray.200"
                pl={{ base: 2, sm: 3 }}
                flexShrink={0}
                minW="80px"
              >
                <Text fontSize="2xs" color="gray.600" mb={0.5} whiteSpace="nowrap">
                  Simples:{' '}
                  <Text as="span" fontWeight="semibold" color="yellow.600">
                    {simplesSemFaturamento}
                  </Text>
                </Text>
                <Text fontSize="2xs" color="gray.600" whiteSpace="nowrap">
                  Lucro:{' '}
                  <Text as="span" fontWeight="semibold" color="teal.600">
                    {lucroSemFaturamento}
                  </Text>
                </Text>
              </Box>
            </Flex>
          )}
        </CardBody>
      </Card>

      <Card
        shadow="sm"
        bg={currentRegime === 'lucro_presumido' ? 'teal.50' : 'white'}
        borderRadius="lg"
        cursor="pointer"
        onClick={() => onSelectRegime('lucro_presumido')}
        _hover={{ shadow: 'md', bg: currentRegime === 'lucro_presumido' ? 'teal.100' : 'gray.50' }}
        border={currentRegime === 'lucro_presumido' ? '2px solid' : '1px solid'}
        borderColor={currentRegime === 'lucro_presumido' ? 'teal.300' : 'gray.200'}
        w={{ base: '100%', sm: '200px' }}
        maxW="200px"
        transition="all 0.2s"
      >
        <CardBody py={3} px={4} position="relative">
          {isLucroPresumidoLoading ? (
            <Flex align="center" justify="center" h="50px">
              <Spinner size="sm" color="#16DBCC" />
            </Flex>
          ) : (
            <>
              <Flex align="center" gap={3}>
                <Box p={1.5} borderRadius="lg" bg="#DCFAF8">
                  <Money color="#16DBCC" size={20} />
                </Box>
                <Box flex={1}>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={0.5}>
                    Lucro Presumido
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="gray.800">
                    {totalLucroPresumido}
                  </Text>
                </Box>
              </Flex>
              {totalEmpresas > 0 && (
                <Text
                  fontSize="xs"
                  color="teal.600"
                  fontWeight="semibold"
                  position="absolute"
                  bottom={2}
                  right={3}
                >
                  {porcentagemPresumido}%
                </Text>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <Card
        shadow="sm"
        bg={currentRegime === 'simples_nacional' ? 'yellow.50' : 'white'}
        borderRadius="lg"
        cursor="pointer"
        onClick={() => onSelectRegime('simples_nacional')}
        _hover={{
          shadow: 'md',
          bg: currentRegime === 'simples_nacional' ? 'yellow.100' : 'gray.50',
        }}
        border={currentRegime === 'simples_nacional' ? '2px solid' : '1px solid'}
        borderColor={currentRegime === 'simples_nacional' ? 'yellow.400' : 'gray.200'}
        w={{ base: '100%', sm: '200px' }}
        maxW="200px"
        transition="all 0.2s"
      >
        <CardBody py={3} px={4} position="relative">
          {isSimplesNacionalLoading ? (
            <Flex align="center" justify="center" h="50px">
              <Spinner size="sm" color="#4CAF50" />
            </Flex>
          ) : (
            <>
              <Flex align="center" gap={3}>
                <Box p={1.5} borderRadius="lg" bg="#FFEB99">
                  <CurrencyDollarSimple color="#4CAF50" size={20} />
                </Box>
                <Box flex={1}>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={0.5}>
                    Simples Nacional
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" color="gray.800">
                    {totalSimplesNacional}
                  </Text>
                </Box>
              </Flex>
              {totalEmpresas > 0 && (
                <Text
                  fontSize="xs"
                  color="yellow.600"
                  fontWeight="semibold"
                  position="absolute"
                  bottom={2}
                  right={3}
                >
                  {porcentagemSimples}%
                </Text>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Card de Alertas */}
      {totalAlertas > 0 && (
        <Card
          shadow="sm"
          bg="red.50"
          borderRadius="lg"
          border="2px solid"
          borderColor="red.300"
          w={{ base: '100%', sm: '280px' }}
          maxW="280px"
          transition="all 0.2s"
        >
          <CardBody py={3} px={4}>
            {isLoading || isLoadingAlternative ? (
              <Flex align="center" justify="center" h="50px">
                <Spinner size="sm" color="red.500" />
              </Flex>
            ) : (
              <Flex align="center" gap={3} justify="space-between">
                <Flex align="center" gap={3}>
                  <Box p={1.5} borderRadius="lg" bg="red.100">
                    <WarningCircle color="#E53E3E" size={20} weight="fill" />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium" mb={0.5}>
                      Alertas Ativos
                    </Text>
                    <Text fontSize="lg" fontWeight="bold" color="red.600">
                      {totalAlertas}
                    </Text>
                  </Box>
                </Flex>
                <Flex direction="column" gap={1} borderLeft="1px" borderColor="red.200" pl={3}>
                  {alertasLimiteExcedido > 0 && (
                    <Tooltip label="Empresas que excederam o limite do regime" hasArrow>
                      <Flex align="center" gap={1}>
                        <Icon as={XCircle} boxSize={3} color="red.600" weight="fill" />
                        <Text fontSize="2xs" color="red.700" fontWeight="semibold">
                          {alertasLimiteExcedido} excedido
                        </Text>
                      </Flex>
                    </Tooltip>
                  )}
                  {alertasLimiteProximo > 0 && (
                    <Tooltip label="Empresas próximas do limite (80%+)" hasArrow>
                      <Flex align="center" gap={1}>
                        <Icon as={WarningCircle} boxSize={3} color="orange.500" weight="fill" />
                        <Text fontSize="2xs" color="orange.600" fontWeight="semibold">
                          {alertasLimiteProximo} próximo
                        </Text>
                      </Flex>
                    </Tooltip>
                  )}
                  {alertasFaturamentoBaixo > 0 && (
                    <Tooltip label="Empresas com faturamento muito baixo" hasArrow>
                      <Flex align="center" gap={1}>
                        <Icon as={TrendDown} boxSize={3} color="yellow.600" weight="fill" />
                        <Text fontSize="2xs" color="yellow.700" fontWeight="semibold">
                          {alertasFaturamentoBaixo} baixo
                        </Text>
                      </Flex>
                    </Tooltip>
                  )}
                </Flex>
              </Flex>
            )}
          </CardBody>
        </Card>
      )}
    </Flex>
  );
}
